import { useGetABOProfileQuery } from "../../../../../../services/AboProfile/AboProfileAPI";
import { useAppSelector } from "../../../../../../store";
import ProfileLoader from "../../../../../../components/ABOProfile/ProfileLoader/ProfileLoader";
import { LeafErrorMessage } from "../../../../../../common/leaf/LeafErrorMessage/LeafErrorMessage";
import "./OverviewTab.scss";
import LeafRectangleLoader from "../../../../../../common/leaf/LeafLoadingSkeleton/LeafRectangleLoader/LeafRectangleLoader";
import type { PartyInfo } from "../../../../../../services/Profile/profileAPI.types";
import ABOProfilePinInfo from "./ABOProfilePinInfo/ABOProfilePinInfo";
import ABOProfilePrimaryAddress from "./ABOProfilePrimaryAddress/ABOProfilePrimaryAddress";
import ABOProfileAdditionalInfo from "./ABOProfileAdditionalInfo/ABOProfileAdditionalInfo";

const OverviewTab = () => {
  const affAbo = useAppSelector((state) => state.profile.affAbo);
  const [aff = "", abo = ""] = affAbo.split("-");
  const isFromEdLos = useAppSelector((state) => state.profile.isFromEdLos);
  const profile = useAppSelector((state) => state.boot?.configuration?.profile);
  const { showHighestPin = false } = profile?.aboInfo || {};

  // fetch profile data
  const {
    isFetching: isFetchingProfile,
    isError: isErrorProfile,
    data: profileData,
  } = useGetABOProfileQuery({ aff, abo, ...(isFromEdLos ? { includePlatinumAndAbove: true } : {}) });

  // Display loading state
  if (isFetchingProfile === true) {
    return (
      <>
        <LeafRectangleLoader isLoading={isFetchingProfile} numRows={1} width="50%" height="1.5rem" />
        <ProfileLoader row={1} card={true} isFetching={isFetchingProfile} />
        <LeafRectangleLoader isLoading={isFetchingProfile} numRows={1} width="50%" height="1.5rem" />
        <ProfileLoader row={3} card={true} isFetching={isFetchingProfile} />
        <LeafRectangleLoader isLoading={isFetchingProfile} numRows={1} width="95%" height="1.5rem" />
        <ProfileLoader row={4} card={true} isFetching={isFetchingProfile} />
      </>
    );
  }

  // Display error state
  if (isErrorProfile === true) {
    return <LeafErrorMessage show={isErrorProfile} />;
  }

  const getApplicantAddress = (partyInfo: PartyInfo[] | undefined) => {
    const defaultAddresses = [
      {
        addressLines: [],
        municipality: "",
        region: "",
        postalCode: "",
        country: "",
        type: "",
      },
    ];
    if (partyInfo && partyInfo.length > 0) {
      const { addresses = defaultAddresses } = partyInfo.find(({ isPrimary }) => isPrimary === true) || partyInfo[0];
      return addresses;
    }
    return defaultAddresses;
  };

  // Profile Data
  const { privacyFlag = false } = profileData?.aboInfo || {};
  const addresses = getApplicantAddress(profileData?.partyInfo);

  const {
    currentPin = 0,
    currentPinDate,
    highestAwardAchieved = 0,
    highestAwardAchievedDate,
    highestTrackingAward = 0,
  } = profileData?.awardInfo || {};

  const {
    autoRenewal = false,
    contractSigned = false,
    orderBlock = false,
    sponsorBlock = false,
  } = profileData?.additionalRegInfo || {};

  const isPlatinumOrAbove = profileData?.isPlatinumOrAbove ?? false;
  const isEmeraldAndAbove = profileData?.isEmeraldAndAbove ?? false;
  const isDiamondAndAbove = profileData?.isDiamondAndAbove ?? false;

  return (
    <div className="overview-tab__container">
      <ABOProfilePrimaryAddress address={addresses[0]} privacyFlag={privacyFlag} />
      <ABOProfilePinInfo
        currentPin={currentPin}
        currentPinDate={currentPinDate}
        highestAwardAchieved={highestAwardAchieved}
        highestAwardAchievedDate={highestAwardAchievedDate}
        highestTrackingAward={highestTrackingAward}
        showHighestPin={showHighestPin ?? false}
        isPlatinumOrAbove={isPlatinumOrAbove}
        isEmeraldAndAbove={isEmeraldAndAbove}
        isDiamondAndAbove={isDiamondAndAbove}
      />
      <ABOProfileAdditionalInfo
        orderBlock={orderBlock}
        sponsorBlock={sponsorBlock}
        autoRenewal={autoRenewal}
        contractSigned={contractSigned}
      />
    </div>
  );
};

export default OverviewTab;
